import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Mail,
  Server,
  Send,
  CheckCircle2,
  Sparkles,
  FileText,
  Shield,
  Clock,
} from 'lucide-react';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import { FieldHelp, setField } from './SettingsUI';

const EMAIL_TEMPLATE_STYLES = [
  { id: 'Classic', hintKey: 'settings.email.hintClassic', tone: '#64748b' },
  { id: 'Modern', hintKey: 'settings.email.hintModern', tone: '#0f172a' },
  { id: 'Professional', hintKey: 'settings.email.hintProfessional', tone: '#1e293b' },
  { id: 'Minimal', hintKey: 'settings.email.hintMinimal', tone: '#94a3b8' },
  { id: 'Executive', hintKey: 'settings.email.hintExecutive', tone: '#1e3a8a' },
  { id: 'Horizon', hintKey: 'settings.email.hintHorizon', tone: '#0f766e' },
];

/** Full EM-02 provider list from product screenshots */
const SMTP_PROVIDERS = [
  { id: 'Quote2Cart Server (default)', host: '', port: 587, encryption: 'tls', isDefault: true },
  { id: 'Gmail, GSuite', host: 'smtp.gmail.com', port: 587, encryption: 'tls' },
  { id: 'Mailgun', host: 'smtp.mailgun.org', port: 587, encryption: 'tls' },
  { id: 'Mandrill', host: 'smtp.mandrillapp.com', port: 587, encryption: 'tls' },
  { id: 'Sendinblue', host: 'smtp-relay.brevo.com', port: 587, encryption: 'tls' },
  { id: 'Sendgrid', host: 'smtp.sendgrid.net', port: 587, encryption: 'tls' },
  { id: 'Elastic Email', host: 'smtp.elasticemail.com', port: 2525, encryption: 'tls' },
  { id: 'SparkPost', host: 'smtp.sparkpostmail.com', port: 587, encryption: 'tls' },
  { id: 'Mailjet', host: 'in-v3.mailjet.com', port: 587, encryption: 'tls' },
  { id: 'Postmark', host: 'smtp.postmarkapp.com', port: 587, encryption: 'tls' },
  { id: 'AOL Mail', host: 'smtp.aol.com', port: 587, encryption: 'tls' },
  { id: 'Comcast', host: 'smtp.comcast.net', port: 587, encryption: 'tls' },
  { id: 'GMX', host: 'mail.gmx.com', port: 587, encryption: 'tls' },
  { id: 'Hotmail', host: 'smtp-mail.outlook.com', port: 587, encryption: 'tls' },
  { id: 'Mail.com', host: 'smtp.mail.com', port: 587, encryption: 'tls' },
  { id: 'O2 Mail', host: 'smtp.o2.co.uk', port: 587, encryption: 'tls' },
  { id: 'Office365', host: 'smtp.office365.com', port: 587, encryption: 'tls' },
  { id: 'Orange', host: 'smtp.orange.fr', port: 587, encryption: 'tls' },
  { id: 'Outlook', host: 'smtp-mail.outlook.com', port: 587, encryption: 'tls' },
  { id: 'Yahoo Mail', host: 'smtp.mail.yahoo.com', port: 587, encryption: 'tls' },
  { id: 'Yahoo Mail Plus', host: 'plus.smtp.mail.yahoo.com', port: 465, encryption: 'ssl' },
  { id: 'Yahoo AU/NZ', host: 'smtp.mail.yahoo.com.au', port: 465, encryption: 'ssl' },
  { id: 'AT&T', host: 'smtp.att.yahoo.com', port: 465, encryption: 'ssl' },
  { id: 'NTL @ntlworld.com', host: 'smtp.ntlworld.com', port: 465, encryption: 'ssl' },
  { id: 'BT Connect', host: 'mail.btconnect.com', port: 25, encryption: 'none' },
  { id: 'Zoho Mail', host: 'smtp.zoho.com', port: 587, encryption: 'tls' },
  { id: 'Verizon', host: 'outgoing.verizon.net', port: 465, encryption: 'ssl' },
  { id: 'BT Openworld', host: 'mail.btinternet.com', port: 465, encryption: 'ssl' },
  { id: 'O2 Online Deutschland', host: 'mail.o2online.de', port: 25, encryption: 'none' },
  { id: 'OVH', host: 'ssl0.ovh.net', port: 465, encryption: 'ssl' },
  { id: 'Rackspace', host: 'secure.emailsrvr.com', port: 465, encryption: 'ssl' },
  { id: 'Custom', host: '', port: 587, encryption: 'tls' },
  { id: 'Quote2Cart Default', host: '', port: 587, encryption: 'tls', isDefault: true, hidden: true },
];

const quillModules = {
  toolbar: [['bold', 'italic', 'underline'], [{ align: [] }], ['clean']],
};

function isCustomProvider(id) {
  return id === 'Custom' || id === 'Custom SMTP';
}

function isDefaultProvider(id) {
  return id === 'Quote2Cart Default' || id === 'Quote2Cart Server (default)';
}

const EmailTab = ({ form, setForm }) => {
  const { t } = useTranslation();
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  const rawProvider = form.smtpProvider || 'Quote2Cart Server (default)';
  const providerId =
    rawProvider === 'Quote2Cart Default' ? 'Quote2Cart Server (default)' : rawProvider;
  const isDefaultSmtp = isDefaultProvider(providerId);

  const visibleProviders = SMTP_PROVIDERS.filter((p) => !p.hidden);
  const knownIds = SMTP_PROVIDERS.map((p) => p.id);
  const providerOptions = knownIds.includes(providerId)
    ? visibleProviders
    : [...visibleProviders, { id: providerId, host: form.smtpHost || '', port: form.smtpPort || 587 }];

  const style = form.emailTemplateStyle || 'Executive';
  const activeStyle = useMemo(
    () => EMAIL_TEMPLATE_STYLES.find((item) => item.id === style) || EMAIL_TEMPLATE_STYLES[4],
    [style],
  );
  const deliveryReady = isDefaultSmtp || Boolean(String(form.smtpHost || '').trim());

  const onProviderChange = (nextId) => {
    const preset =
      SMTP_PROVIDERS.find((p) => p.id === nextId) || {
        id: nextId,
        host: '',
        port: 587,
        encryption: 'tls',
      };
    setForm((prev) => ({
      ...prev,
      smtpProvider: preset.id,
      smtpHost: isCustomProvider(preset.id) ? prev.smtpHost || '' : preset.host,
      smtpPort: isCustomProvider(preset.id) ? prev.smtpPort || 587 : preset.port,
      smtpEncryption: isCustomProvider(preset.id)
        ? prev.smtpEncryption || 'tls'
        : preset.encryption || 'tls',
    }));
  };

  const sendTest = async () => {
    if (!testEmail.trim()) {
      showToast(t('settings.email.toast.enterEmail'), 'warning');
      return;
    }
    setTesting(true);
    try {
      await api.testEmail(testEmail.trim());
      showToast(t('settings.email.toast.testSent', { email: testEmail }), 'success');
    } catch (err) {
      showToast(err.message || t('settings.email.toast.testFailed'), 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="q2c-email-tab">
      <section className="q2c-email-hero">
        <div className="q2c-email-hero-copy">
          <div className="q2c-email-hero-kicker">
            <Sparkles size={14} />
            {t('settings.email.heroKicker')}
          </div>
          <h3 className="q2c-email-hero-title">{t('settings.email.heroTitle')}</h3>
          <p className="q2c-email-hero-sub">
            {t('settings.email.heroSub')}
          </p>
          <ol className="q2c-email-steps" aria-label={t('settings.email.heroKicker')}>
            <li>
              <span>1</span> {t('settings.email.stepDeliver')}
            </li>
            <li>
              <span>2</span> {t('settings.email.stepNotify')}
            </li>
            <li>
              <span>3</span> {t('settings.email.stepStyle')}
            </li>
            <li>
              <span>4</span> {t('settings.email.stepWrite')}
            </li>
          </ol>
        </div>
        <div className={`q2c-email-status${deliveryReady ? ' is-ready' : ''}`}>
          <div className="q2c-email-status-top">
            <Server size={18} />
            <div>
              <div className="q2c-email-status-label">{t('settings.email.delivery')}</div>
              <div className="q2c-email-status-value">{providerId}</div>
            </div>
          </div>
          <div className="q2c-email-status-pill">
            {deliveryReady ? (
              <>
                <CheckCircle2 size={14} /> {t('settings.email.readyToSend')}
              </>
            ) : (
              <>
                <Server size={14} /> {t('settings.email.finishSmtp')}
              </>
            )}
          </div>
        </div>
      </section>

      <div className="row g-3">
        <div className="col-xl-5 d-flex flex-column gap-3">
          <section className="q2c-email-panel">
            <header className="q2c-email-panel-head">
              <span className="q2c-email-step-badge">1</span>
              <div>
                <h6 className="mb-0">{t('settings.email.delivery')}</h6>
                <p className="mb-0">{t('settings.email.deliveryPanelSub')}</p>
              </div>
            </header>

            <FieldHelp
              label={t('settings.email.emailProvider')}
              help={t('settings.email.emailProviderHelp')}
            >
              <Form.Select value={providerId} onChange={(e) => onProviderChange(e.target.value)}>
                {providerOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.id}
                  </option>
                ))}
              </Form.Select>
            </FieldHelp>

            {!isDefaultSmtp && (
              <div className="q2c-email-smtp-grid">
                <FieldHelp label={t('settings.email.smtpHost')}>
                  <Form.Control
                    value={form.smtpHost || ''}
                    onChange={(e) => setField(setForm, 'smtpHost', e.target.value)}
                    placeholder={t('settings.email.smtpHostPlaceholder')}
                  />
                </FieldHelp>
                <div className="row g-2">
                  <div className="col-6">
                    <FieldHelp label={t('settings.email.port')}>
                      <Form.Control
                        type="number"
                        value={form.smtpPort ?? 587}
                        onChange={(e) =>
                          setField(setForm, 'smtpPort', parseInt(e.target.value, 10) || 587)
                        }
                      />
                    </FieldHelp>
                  </div>
                  <div className="col-6">
                    <FieldHelp label={t('settings.email.encryption')}>
                      <Form.Select
                        value={form.smtpEncryption || 'tls'}
                        onChange={(e) => setField(setForm, 'smtpEncryption', e.target.value)}
                      >
                        <option value="none">{t('settings.email.encNone')}</option>
                        <option value="tls">{t('settings.email.encTls')}</option>
                        <option value="ssl">{t('settings.email.encSsl')}</option>
                      </Form.Select>
                    </FieldHelp>
                  </div>
                </div>
                <FieldHelp label={t('settings.email.smtpUser')}>
                  <Form.Control
                    value={form.smtpUser || ''}
                    onChange={(e) => setField(setForm, 'smtpUser', e.target.value)}
                    autoComplete="off"
                  />
                </FieldHelp>
                <FieldHelp
                  label={t('settings.email.smtpPassword')}
                  help={
                    form.smtpPassConfigured
                      ? t('settings.email.smtpPasswordHelpConfigured')
                      : t('settings.email.smtpPasswordHelp')
                  }
                >
                  <Form.Control
                    type="password"
                    value={form.smtpPass || ''}
                    onChange={(e) => setField(setForm, 'smtpPass', e.target.value)}
                    autoComplete="new-password"
                    placeholder={
                      form.smtpPassConfigured ? t('settings.email.smtpPasswordPlaceholderConfigured') : ''
                    }
                  />
                </FieldHelp>
              </div>
            )}

            <div className="q2c-email-test">
              <div className="q2c-email-test-label">
                <Send size={15} />
                {t('settings.email.sendTest')}
              </div>
              <div className="q2c-email-test-row">
                <Form.Control
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder={t('settings.email.testPlaceholder')}
                />
                <Button
                  type="button"
                  variant="primary"
                  className="text-nowrap"
                  onClick={sendTest}
                  disabled={testing}
                >
                  {testing ? t('settings.email.sending') : t('settings.email.sendTestButton')}
                </Button>
              </div>
              <p className="q2c-email-hint">{t('settings.email.saveBeforeTest')}</p>
            </div>
          </section>

          <section className="q2c-email-panel">
            <header className="q2c-email-panel-head">
              <span className="q2c-email-step-badge">2</span>
              <div>
                <h6 className="mb-0">{t('settings.email.whoGetsMail')}</h6>
                <p className="mb-0">{t('settings.email.notifyPanelSub')}</p>
              </div>
            </header>

            <FieldHelp
              label={t('settings.email.subjectPrefix')}
              help={t('settings.email.subjectPrefixHelp')}
            >
              <Form.Control
                value={form.subjectPrefix || ''}
                onChange={(e) => setField(setForm, 'subjectPrefix', e.target.value)}
                placeholder="e.g. Your quote —"
              />
            </FieldHelp>
            <FieldHelp
              label={t('settings.email.newQuoteNotify')}
              help={t('settings.email.newQuoteNotifyHelp')}
            >
              <Form.Control
                value={form.notifyEmails || ''}
                onChange={(e) => setField(setForm, 'notifyEmails', e.target.value)}
                placeholder={t('settings.email.newQuoteNotifyPlaceholder')}
              />
            </FieldHelp>
            <div className="q2c-email-store-intro">
              <div className="q2c-email-copy-pane-title">
                <Mail size={16} />
                <span>{t('settings.email.storeIntro')}</span>
              </div>
              <p className="q2c-email-hint mb-2">{t('settings.email.storeIntroHelp')}</p>
              <div className="q2c-quill">
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={form.emailNewQuoteHtml || ''}
                  onChange={(v) => setField(setForm, 'emailNewQuoteHtml', v)}
                  placeholder={t('settings.email.paneNewPlaceholder')}
                />
              </div>
            </div>
            <FieldHelp
              label={t('settings.email.bcc')}
              help={t('settings.email.bccHelp')}
            >
              <Form.Control
                value={form.bccEmails || ''}
                onChange={(e) => setField(setForm, 'bccEmails', e.target.value)}
                placeholder={t('settings.email.bccPlaceholder')}
              />
            </FieldHelp>
            <FieldHelp
              label={t('settings.email.reminderDays')}
              help={t('settings.email.reminderDaysHelp')}
            >
              <Form.Control
                type="number"
                min="0"
                value={form.reminderDays ?? 7}
                onChange={(e) =>
                  setField(setForm, 'reminderDays', parseInt(e.target.value, 10) || 0)
                }
              />
            </FieldHelp>
          </section>
        </div>

        <div className="col-xl-7 d-flex flex-column gap-3">
          <section className="q2c-email-panel q2c-email-panel--look">
            <header className="q2c-email-panel-head">
              <span className="q2c-email-step-badge">3</span>
              <div>
                <h6 className="mb-0">{t('settings.email.look')}</h6>
                <p className="mb-0">{t('settings.email.lookPanelSub')}</p>
              </div>
            </header>

            <div className="q2c-email-look-stage">
              <div
                className={`q2c-template-preview q2c-email-look-stage-preview q2c-template-${activeStyle.id.toLowerCase()}`}
                aria-hidden="true"
              />
              <div className="q2c-email-look-stage-meta">
                <div className="q2c-email-look-stage-name">{activeStyle.id}</div>
                <div className="q2c-email-look-stage-hint">{t(activeStyle.hintKey)}</div>
                <div
                  className="q2c-email-look-stage-swatch"
                  style={{ background: activeStyle.tone }}
                />
              </div>
            </div>

            <div className="q2c-email-style-grid" role="radiogroup" aria-label={t('settings.email.look')}>
              {EMAIL_TEMPLATE_STYLES.map((item) => {
                const active = style === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`q2c-email-style-card${active ? ' is-active' : ''}`}
                    onClick={() => setField(setForm, 'emailTemplateStyle', item.id)}
                  >
                    <span
                      className={`q2c-template-preview q2c-template-${item.id.toLowerCase()}`}
                    />
                    <span className="q2c-email-style-name">{item.id}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="q2c-email-panel q2c-email-panel--copy">
            <header className="q2c-email-panel-head">
              <span className="q2c-email-step-badge">4</span>
              <div>
                <h6 className="mb-0">{t('settings.email.copy')}</h6>
                <p className="mb-0">{t('settings.email.copyPanelSub')}</p>
              </div>
            </header>

            <div className="q2c-email-copy-pane q2c-email-copy-block">
              <div className="q2c-email-copy-pane-title">
                <Mail size={16} />
                <span>{t('settings.email.requestTitle')}</span>
              </div>
              <p className="q2c-email-hint mb-2">{t('settings.email.requestHelp')}</p>
              <Form.Check
                type="switch"
                id="q2c-request-email-enabled"
                className="mb-2"
                label={t('settings.email.requestSend')}
                checked={form.requestEmailEnabled !== false}
                onChange={(e) => setField(setForm, 'requestEmailEnabled', e.target.checked)}
              />
              <FieldHelp label={t('settings.email.requestSubject')}>
                <Form.Control
                  value={form.requestEmailSubject || ''}
                  onChange={(e) => setField(setForm, 'requestEmailSubject', e.target.value)}
                  placeholder={t('settings.email.requestSubjectPlaceholder')}
                />
              </FieldHelp>
              <div className="q2c-quill">
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={form.emailConfirmationHtml || ''}
                  onChange={(v) => setField(setForm, 'emailConfirmationHtml', v)}
                  placeholder={t('settings.email.paneConfirmPlaceholder')}
                />
              </div>
            </div>

            <div className="q2c-email-copy-pane q2c-email-copy-block">
              <div className="q2c-email-copy-pane-title">
                <FileText size={16} />
                <span>{t('settings.email.approvalTitle')}</span>
              </div>
              <p className="q2c-email-hint mb-2">{t('settings.email.approvalHelp')}</p>
              <Form.Check
                type="switch"
                id="q2c-approval-email-enabled"
                className="mb-2"
                label={t('settings.email.approvalSend')}
                checked={form.approvalEmailEnabled !== false}
                onChange={(e) => setField(setForm, 'approvalEmailEnabled', e.target.checked)}
              />
              <FieldHelp
                label={t('settings.email.approvalSubject')}
                help={t('settings.email.approvalSubjectHelp')}
              >
                <Form.Control
                  value={form.approvalEmailSubject || ''}
                  onChange={(e) => setField(setForm, 'approvalEmailSubject', e.target.value)}
                  placeholder={form.subjectPrefix || t('settings.email.approvalSubjectPlaceholder')}
                />
              </FieldHelp>
            </div>

            <div className="q2c-email-copy-pane q2c-email-copy-block">
              <div className="q2c-email-copy-pane-title">
                <Shield size={16} />
                <span>{t('settings.email.termsTitle')}</span>
              </div>
              <p className="q2c-email-hint mb-2">{t('settings.email.termsHelp')}</p>
              <div className="q2c-quill">
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={form.emailTermsHtml || ''}
                  onChange={(v) => setField(setForm, 'emailTermsHtml', v)}
                  placeholder={t('settings.email.paneTermsPlaceholder')}
                />
              </div>
            </div>

            <div className="q2c-email-copy-pane q2c-email-copy-block">
              <div className="q2c-email-copy-pane-title">
                <Clock size={16} />
                <span>{t('settings.email.reminderTitle')}</span>
              </div>
              <p className="q2c-email-hint mb-2">{t('settings.email.reminderHelp')}</p>
              <div className="q2c-quill">
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={form.emailReminderHtml || ''}
                  onChange={(v) => setField(setForm, 'emailReminderHtml', v)}
                  placeholder={t('settings.email.paneReminderPlaceholder')}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default EmailTab;
